import React from 'react';

import { stepOptionsMatch, StepSelectorOption } from './stepConfig';
import { CheckedSelections } from './useCheckedSelections';

/** A click or keypress; only its Ctrl/Cmd state matters for the multi-select rules. */
export type ModifierEvent = Pick<React.MouseEvent, 'ctrlKey' | 'metaKey'>;

export const isModifierClick = (event?: ModifierEvent): boolean =>
  !!event?.ctrlKey || !!event?.metaKey;

interface PickerFlyoutRulesArgs {
  enableMultiSelect: boolean;
  isFlyoutOpen: boolean;
  highlightedItem: StepSelectorOption | null;
  setHighlightedItem: (item: StepSelectorOption) => void;
  selections: CheckedSelections;
}

/**
 * Multi-select rules shared by the Seed and Seed Treatment rows and their flyouts.
 * - Rows: Ctrl/Cmd+click locks multi-select only while no flyout is open; once locked (or with
 *   anything checked) every click follows the multi-select rules.
 * - Flyout Add / Pick Variety Later / variety pick: Ctrl/Cmd+click (or a lock) stages instead of committing.
 */
export const createPickerFlyoutRules = ({
  enableMultiSelect,
  isFlyoutOpen,
  highlightedItem,
  setHighlightedItem,
  selections,
}: PickerFlyoutRulesArgs) => {
  const isMultiSelectRowClick = (event?: ModifierEvent) =>
    enableMultiSelect &&
    (selections.isLockedOrHasChecked ||
      (isModifierClick(event) && !isFlyoutOpen));

  const isMultiSelectFlyoutClick = (event?: ModifierEvent) =>
    enableMultiSelect &&
    (selections.isLockedOrHasChecked || isModifierClick(event));

  const isFlyoutOpenFor = (item: StepSelectorOption) =>
    isFlyoutOpen &&
    !!highlightedItem &&
    stepOptionsMatch(highlightedItem, item);

  /** Unchecks a row; its own flyout closes, while another row's open flyout keeps the highlight. */
  const uncheckRow = (item: StepSelectorOption, closeOwnFlyout: () => void) => {
    const isOtherFlyoutOpen = isFlyoutOpen && !isFlyoutOpenFor(item);
    if (isFlyoutOpenFor(item)) closeOwnFlyout();
    selections.toggleCheckedItem(item);
    if (!isOtherFlyoutOpen) setHighlightedItem(item);
  };

  return {
    isMultiSelectRowClick,
    isMultiSelectFlyoutClick,
    isFlyoutOpenFor,
    uncheckRow,
  };
};

export type PickerFlyoutRules = ReturnType<typeof createPickerFlyoutRules>;

/** What the Seed and Seed Treatment flyout hooks need from StepInputSelector. */
export interface PickerFlyoutHost {
  enableMultiSelect: boolean;
  filteredOptions: StepSelectorOption[];
  highlightedItem: StepSelectorOption | null;
  setHighlightedItem: (item: StepSelectorOption) => void;
  isFlyoutOpen: boolean;
  setIsFlyoutOpen: (open: boolean) => void;
  selections: CheckedSelections;
  rules: PickerFlyoutRules;
  commitSingleSelect: (item: StepSelectorOption) => void;
}
